import { Component } from '@theme/component';
import { debounce, isClickedOutside } from '@theme/utilities';

// Több megnyitott ablak esetén csak az utolsó bezárásakor
// engedjük vissza az oldal görgetését.
const scrollOwners = new Set();
let savedBodyState = null;

function lockPage(owner) {
  if (scrollOwners.has(owner)) return;

  if (scrollOwners.size === 0) {
    const body = document.body;

    savedBodyState = {
      x: window.scrollX,
      y: window.scrollY,
      styles: ['width', 'position', 'top'].map((name) => ({
        name,
        value: body.style.getPropertyValue(name),
        priority: body.style.getPropertyPriority(name),
      })),
    };

    body.style.width = '100%';
    body.style.position = 'fixed';
    body.style.top = `-${savedBodyState.y}px`;
  }

  scrollOwners.add(owner);
}

function unlockPage(owner) {
  if (!scrollOwners.delete(owner)) return;
  if (scrollOwners.size > 0 || !savedBodyState) return;

  const saved = savedBodyState;
  savedBodyState = null;

  for (const { name, value, priority } of saved.styles) {
    if (value) {
      document.body.style.setProperty(name, value, priority);
    } else {
      document.body.style.removeProperty(name);
    }
  }

  window.scrollTo({
    left: saved.x,
    top: saved.y,
    behavior: 'instant',
  });
}

function updateScrollLockAttribute() {
  const hasOpenPanel = document.querySelector(
    'dialog[scroll-lock][open], details[scroll-lock][open]'
  );

  document.documentElement.toggleAttribute(
    'scroll-lock',
    Boolean(hasOpenPanel)
  );
}

// A bezárás nem várhat korlátlanul egy animációra.
async function waitForCloseAnimation(dialog) {
  let timeout;

  try {
    // A frissen hozzáadott CSS-osztály animációinak kiértékelése.
    void dialog.offsetWidth;

    const animations = dialog.getAnimations({ subtree: false });
    if (!animations.length) return;

    await Promise.race([
      Promise.allSettled(
        animations.map((animation) => animation.finished)
      ),
      new Promise((resolve) => {
        timeout = window.setTimeout(resolve, 700);
      }),
    ]);
  } finally {
    window.clearTimeout(timeout);
  }
}

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  #session = null;
  #listenerTimer = null;
  #dialog = null;

  connectedCallback() {
    super.connectedCallback();

    this.#dialog = this.refs.dialog;
    this.#dialog.addEventListener('close', this.#handleNativeClose);
    this.#dialog.addEventListener('cancel', this.#handleCancel);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#handleResize);

    const dialog = this.#dialog;

    if (dialog) {
      dialog.removeEventListener('close', this.#handleNativeClose);
      dialog.removeEventListener('cancel', this.#handleCancel);

      if (dialog.open) {
        dialog.close();
      }
    }

    this.#finishClose();
    this.#dialog = null;

    super.disconnectedCallback();
  }

  #handleResize = debounce(() => {
    if (!this.isConnected) return;

    const { minWidth, maxWidth } = this;
    const width = window.innerWidth;

    if (
      (minWidth > 0 && width < minWidth) ||
      (maxWidth > 0 && width > maxWidth)
    ) {
      this.closeDialog();
    }
  }, 50);

  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Egy korábbi, közvetlen bezárás takarítása.
    this.#finishClose();

    const session = { closing: false };
    this.#session = session;

    dialog.classList.remove('dialog-closing');
    lockPage(this);

    try {
      dialog.showModal();
    } catch (error) {
      this.#session = null;
      unlockPage(this);
      throw error;
    }

    updateScrollLockAttribute();

    // Ne kezeljük kívülre kattintásként a megnyitó kattintást.
    this.#listenerTimer = window.setTimeout(() => {
      this.#listenerTimer = null;

      if (
        this.#session !== session ||
        session.closing ||
        !dialog.open ||
        !this.isConnected
      ) {
        return;
      }

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    }, 0);

    this.dispatchEvent(new DialogOpenEvent());
  }

  closeDialog = async () => {
    const { dialog } = this.refs;
    const session = this.#session;

    if (!dialog.open) {
      this.#finishClose();
      return;
    }

    if (session?.closing) return;

    if (session) {
      session.closing = true;
    }

    this.#removeInteractionListeners();
    dialog.classList.add('dialog-closing');

    try {
      await waitForCloseAnimation(dialog);
    } finally {
      // Egy régi bezárás ne zárjon be egy újra megnyitott ablakot.
      if (this.#session === session) {
        try {
          if (dialog.open) {
            dialog.close();
          }
        } finally {
          this.#finishClose();
        }
      }
    }
  };

  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  #removeInteractionListeners() {
    window.clearTimeout(this.#listenerTimer);
    this.#listenerTimer = null;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
  }

  #finishClose() {
    const hadSession = this.#session !== null;
    this.#session = null;

    this.#removeInteractionListeners();
    this.#dialog?.classList.remove('dialog-closing');

    unlockPage(this);
    updateScrollLockAttribute();

    if (hadSession) {
      this.dispatchEvent(new DialogCloseEvent());
    }
  }

  #handleNativeClose = () => {
    // Közben újra megnyitott ablakot ne takarítsunk el.
    if (this.#dialog?.open) return;

    this.#finishClose();
  };

  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  #handleClick = (event) => {
    if (isClickedOutside(event, this.refs.dialog)) {
      this.closeDialog();
    }
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) {
  customElements.define('dialog-component', DialogComponent);
}

export class DialogOpenEvent extends CustomEvent {
  static eventName = 'dialog:open';

  constructor() {
    super(DialogOpenEvent.eventName);
  }
}

export class DialogCloseEvent extends CustomEvent {
  static eventName = 'dialog:close';

  constructor() {
    super(DialogCloseEvent.eventName);
  }
}

document.addEventListener(
  'toggle',
  (event) => {
    if (
      event.target instanceof HTMLDialogElement ||
      event.target instanceof HTMLDetailsElement
    ) {
      updateScrollLockAttribute();
    }
  },
  { capture: true }
);